/**
 * @relatedFiles ../client-layout.tsx, ./LandingPageOld.tsx, ../../app/layout.tsx
 * @purpose Public landing page with animated branding and sign-in functionality for unauthenticated users
 * @notes Contains animated blob backgrounds, letter-by-letter text animation, and Clerk sign-in integration
 */
"use client";

import { SignInButton } from "@clerk/nextjs";
import React from "react";
export function LandingPage() {
  return (
    <>
      {/* Background visuals */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-50 to-white" />
        <div className="absolute top-[-30%] md:top-[-30%] left-[-10%] w-[600px] h-[600px] rounded-full bg-purple-500/30 blur-[120px] animate-blob" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[30%] rounded-full bg-red-500/30 blur-[120px] animate-blob animation-delay-2000" />
        <div className="absolute bottom-[30%] right-[10%] w-[60%] h-[50%] rounded-full bg-teal-300/30 blur-[120px] animate-blob animation-delay-4000" />
        <div className="absolute bottom-[10%] left-[20%] w-[400px] h-[500px] rounded-full bg-green-500/20 blur-[120px] animate-blob animation-delay-6000" />
      </div>

      {/* Foreground content */}
      <div className="splash-screen fixed inset-0 flex items-center justify-center">
        <div className="max-w-4xl px-8">
          <div className="page-content opacity-100 flex flex-col items-center text-center space-y-12">
            {/* Brand word-mark */}
            <div className="flex flex-col items-center">
              <h1 className="sr-only">Snow Chat</h1>
              <div className="space-y-4 relative select-none pointer-events-none">
                <div className="text-5xl sm:text-7xl font-bold text-gray-900 tracking-tight leading-tight flex">
                  {"Snow Chat".split("").map((letter, index) => (
                    <span
                      key={index}
                      className="animate-letter"
                      style={{
                        animationDelay: `${index * 50}ms`,
                        display: letter === " " ? "inline-block" : "inline",
                        width: letter === " " ? "0.3em" : "auto",
                      }}
                    >
                      {letter}
                    </span>
                  ))}
                </div>
                {/* Outline / stroke for depth */}
                <span
                  className="text-5xl sm:text-7xl font-bold text-gray-900 tracking-tight leading-tight absolute -z-10 top-0 left-0 !m-0 flex"
                  style={{ WebkitTextStroke: "3px #ffffff73", textShadow: "0 0 20px #5c5c5c50" }}
                >
                  {"Snow Chat".split("").map((letter, index) => (
                    <span
                      key={index}
                      className="animate-letter"
                      style={{
                        animationDelay: `${index * 50}ms`,
                        display: letter === " " ? "inline-block" : "inline",
                        width: letter === " " ? "0.3em" : "auto",
                      }}
                    >
                      {letter}
                    </span>
                  ))}
                </span>
              </div>
            </div>

            {/* Tag-line */}
            <p className="text-xl sm:text-3xl text-gray-700 font-bold animate-fade-blur" style={{ animationDelay: "450ms" }}>
              Clarity in Action
            </p>

            {/* Short description */}
            <p className="text-lg sm:text-xl text-gray-600 font-bold max-w-xl animate-simple-fade" style={{ animationDelay: ".7s" }}>
              This is our first step in creating a truly personal intelligent assistant.
            </p>

            {/* CTA */}
            <SignInButton mode="modal">
              <button
                className="hover:outline hover:outline-2 outline-white/30 mt-6 font-bold px-8 py-4 text-lg text-black transition-all rounded-xl bg-blue-200 hover:bg-blue-100 focus:ring-2 focus:ring-blue-100 focus:ring-offset-2 shadow-lg shadow-blue-100 animate-simple-fade"
                style={{ animationDelay: "1s" }}
              >
                Sign In
              </button>
            </SignInButton>

            {/* Privacy link */}
            <a
              href="/privacy"
              target="_blank"
              className="hidden absolute bottom-4 text-sm text-gray-600 hover:text-gray-700 transition-colors animate-simple-fade"
              style={{ animationDelay: "1.2s" }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

export default LandingPage; 